import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Link,
  Chip,
  Grid,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  LocalShipping as ShippingIcon,
  QrCode as QrCodeIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  ContentCopy as ContentCopyIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as DeliveredIcon,
  HourglassEmpty as PendingIcon,
  DirectionsBus as InTransitIcon,
  Cancel as CancelledIcon,
  FlightTakeoff as ExpressIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { shipmentService } from '../../services/shipmentService';

interface ShipmentSectionProps {
  orderId: string;
  orderNumber?: string;
  awbNumber?: string;
  trackingUrl?: string;
  shipmentStatus?: string;
  carrierName?: string;
  onShipmentCreated?: () => void;
}

const STATUS_STEPS = [
  { key: 'created', label: 'Created' },
  { key: 'in_transit', label: 'In Transit' },
  { key: 'out_for_delivery', label: 'Out for Delivery' },
  { key: 'delivered', label: 'Delivered' }
];

const getActiveStep = (status?: string) => {
  if (!status) return 0;
  const s = status.toLowerCase();
  if (s.includes('deliver')) return 3;
  if (s.includes('out') || s.includes('reach')) return 2;
  if (s.includes('transit') || s.includes('picked') || s.includes('shipped')) return 1;
  return 0;
};

const ShipmentSection: React.FC<ShipmentSectionProps> = ({
  orderId,
  orderNumber,
  awbNumber,
  trackingUrl,
  shipmentStatus,
  carrierName,
  onShipmentCreated
}) => {
  const { enqueueSnackbar } = useSnackbar();
  
  // Carrier selection: delhivery | shiprocket
  const [carrier, setCarrier] = useState<'delhivery' | 'shiprocket'>('delhivery');
  const [warehouse, setWarehouse] = useState<'NOIDA' | 'NIMIYA'>('NOIDA');
  const [pickupLocation, setPickupLocation] = useState<string>('Primary');
  
  // Dimensions
  const [weight, setWeight] = useState<string>('1.0');
  const [length, setLength] = useState<string>('30');
  const [width, setWidth] = useState<string>('20');
  const [height, setHeight] = useState<string>('15');
  
  // States
  const [loading, setLoading] = useState(false);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [labelUrl, setLabelUrl] = useState<string | null>(null);
  const [trackDialogOpen, setTrackDialogOpen] = useState(false);
  const [liveTrackingInfo, setLiveTrackingInfo] = useState<any>(null);

  const handleCopyAWB = () => {
    if (awbNumber) {
      navigator.clipboard.writeText(awbNumber);
      enqueueSnackbar('AWB copied to clipboard', { variant: 'info' });
    }
  };

  const handleCreateShipment = async () => {
    try {
      setLoading(true);
      
      const weightNum = parseFloat(weight);
      const lengthNum = parseFloat(length);
      const widthNum = parseFloat(width);
      const heightNum = parseFloat(height);
      
      if (isNaN(weightNum) || weightNum <= 0) {
        enqueueSnackbar('Please enter a valid weight', { variant: 'error' });
        return;
      }
      
      if (isNaN(lengthNum) || lengthNum <= 0 || isNaN(widthNum) || widthNum <= 0 || isNaN(heightNum) || heightNum <= 0) {
        enqueueSnackbar('Please enter valid dimensions', { variant: 'error' });
        return;
      }
      
      let result;
      if (carrier === 'delhivery') {
        result = await shipmentService.createShipment(orderId, {
          warehouse_name: warehouse,
          weight: weightNum,
          length: lengthNum,
          width: widthNum,
          height: heightNum
        });
      } else {
        result = await shipmentService.createShiprocketShipment(orderId, {
          pickup_location: pickupLocation || 'Primary',
          weight: weightNum,
          length: lengthNum,
          width: widthNum,
          height: heightNum
        });
      }

      enqueueSnackbar(result.message || `Shipment created successfully via ${carrier === 'delhivery' ? 'Delhivery' : 'Shiprocket'}`, { variant: 'success' });
      
      if (result.label_url) {
        setLabelUrl(result.label_url);
      }

      if (onShipmentCreated) {
        onShipmentCreated();
      }
    } catch (error: any) {
      console.error('Failed to create shipment:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create shipment';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleTrackShipment = async () => {
    try {
      setTrackingLoading(true);
      const res = await shipmentService.trackShipment(orderId);
      setLiveTrackingInfo(res.tracking_info);
      setTrackDialogOpen(true);
      enqueueSnackbar('Tracking information updated', { variant: 'success' });
      
      if (onShipmentCreated) {
        onShipmentCreated();
      }
    } catch (error: any) {
      console.error('Failed to track shipment:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to track shipment';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setTrackingLoading(false);
    }
  };

  const handleDownloadLabel = async () => {
    try {
      if (labelUrl) {
        window.open(labelUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      const result = await shipmentService.getShipmentLabel(orderId);
      
      if (result.label_url) {
        setLabelUrl(result.label_url);
        window.open(result.label_url, '_blank', 'noopener,noreferrer');
      }
    } catch (error: any) {
      console.error('Failed to get label:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to get shipping label';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  // If shipment already exists, show tracking info
  if (awbNumber) {
    const isShiprocket = (carrierName || '').toLowerCase().includes('shiprocket');
    const isDelhivery = !isShiprocket;
    const activeStep = getActiveStep(shipmentStatus);

    return (
      <Card sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ShippingIcon sx={{ color: isDelhivery ? '#1565c0' : '#c2185b' }} />
              <Typography variant="h6" fontWeight={600}>
                Shipment &amp; Logistics
              </Typography>
            </Stack>
            <Chip
              label={carrierName || (isShiprocket ? 'Shiprocket' : 'Delhivery')}
              size="small"
              sx={{
                bgcolor: isDelhivery ? '#e3f2fd' : '#fce4ec',
                color: isDelhivery ? '#1565c0' : '#c2185b',
                fontWeight: 700,
                fontSize: 12
              }}
            />
          </Box>

          {/* Stepper overview */}
          <Box sx={{ my: 2, px: 1 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {STATUS_STEPS.map((step) => (
                <Step key={step.key}>
                  <StepLabel>{step.label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                AWB / Tracking Number
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
                <Typography variant="body1" fontFamily="monospace" fontWeight={700}>
                  {awbNumber}
                </Typography>
                <Tooltip title="Copy AWB">
                  <IconButton size="small" onClick={handleCopyAWB}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Current Status
              </Typography>
              <Box mt={0.5}>
                <Chip 
                  label={(shipmentStatus || 'Created').toUpperCase()} 
                  color={
                    (shipmentStatus || '').toLowerCase().includes('deliver') ? 'success' :
                    (shipmentStatus || '').toLowerCase().includes('transit') || (shipmentStatus || '').toLowerCase().includes('out') ? 'warning' : 'primary'
                  }
                  size="small" 
                />
              </Box>
            </Grid>
          </Grid>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mt={3}>
            <Button
              variant="contained"
              color="primary"
              startIcon={trackingLoading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              onClick={handleTrackShipment}
              disabled={trackingLoading}
              size="small"
            >
              {trackingLoading ? 'Checking...' : 'Live Track'}
            </Button>

            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handleDownloadLabel}
              size="small"
            >
              Shipping Label / Slip
            </Button>

            {trackingUrl && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<OpenInNewIcon />}
                component="a"
                href={trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
              >
                Track on {carrierName || 'Carrier'}
              </Button>
            )}
          </Stack>
        </CardContent>

        {/* Live Track Modal */}
        <Dialog open={trackDialogOpen} onClose={() => setTrackDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Live Tracking — {awbNumber}
            <Typography variant="caption" color="text.secondary" display="block">
              Carrier: {carrierName || 'Logistics'} · Order: {orderNumber || orderId}
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            {liveTrackingInfo ? (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Latest Status: <strong>{liveTrackingInfo.status || liveTrackingInfo.data?.status || shipmentStatus || 'In Transit'}</strong>
                </Typography>
                {(liveTrackingInfo.current_location || liveTrackingInfo.data?.current_location) && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Location: {liveTrackingInfo.current_location || liveTrackingInfo.data?.current_location}
                  </Typography>
                )}
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" mb={1}>Checkpoints History</Typography>
                {((liveTrackingInfo.tracking_history || liveTrackingInfo.data?.tracking_history || [])).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No tracking events recorded yet.</Typography>
                ) : (
                  (liveTrackingInfo.tracking_history || liveTrackingInfo.data?.tracking_history || []).map((evt: any, i: number) => (
                    <Box key={i} sx={{ mb: 1.5, pl: 2, borderLeft: '3px solid #1976d2' }}>
                      <Typography variant="caption" color="text.secondary">{evt.timestamp || evt.date}</Typography>
                      <Typography variant="body2" fontWeight={500}>{evt.description || evt.status || evt.activity}</Typography>
                      {evt.location && <Typography variant="caption" color="text.secondary">{evt.location}</Typography>}
                    </Box>
                  ))
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Tracking details loaded. Status: {shipmentStatus || 'Created'}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            {trackingUrl && (
              <Button
                component="a"
                href={trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<OpenInNewIcon />}
                size="small"
              >
                Carrier Website
              </Button>
            )}
            <Button onClick={() => setTrackDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Card>
    );
  }

  // If no shipment, show create shipment form
  return (
    <Card sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ShippingIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Create Shipment
            </Typography>
          </Stack>
          <Chip label="Ready to Dispatch" size="small" color="info" variant="outlined" />
        </Box>

        <Alert severity="info" sx={{ mb: 2.5 }}>
          Generate an instant AWB and shipping label with Delhivery or Shiprocket.
        </Alert>

        {/* Carrier Selection Toggle */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Select Logistics Carrier
        </Typography>
        <ToggleButtonGroup
          value={carrier}
          exclusive
          onChange={(_, val) => { if (val) setCarrier(val); }}
          fullWidth
          size="small"
          sx={{ mb: 2.5 }}
        >
          <ToggleButton value="delhivery" sx={{ py: 1, fontWeight: 600 }}>
            🚚 Delhivery (Direct B2C / Express)
          </ToggleButton>
          <ToggleButton value="shiprocket" sx={{ py: 1, fontWeight: 600 }}>
            🚀 Shiprocket (Aggregator)
          </ToggleButton>
        </ToggleButtonGroup>

        {carrier === 'delhivery' ? (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Dispatch Warehouse</InputLabel>
            <Select
              value={warehouse}
              label="Dispatch Warehouse"
              onChange={(e) => setWarehouse(e.target.value as 'NOIDA' | 'NIMIYA')}
              disabled={loading}
            >
              <MenuItem value="NOIDA">NOIDA — Sector 62, Noida, Uttar Pradesh (201301)</MenuItem>
              <MenuItem value="NIMIYA">NIMIYA — Daltonganj, Jharkhand (822101)</MenuItem>
            </Select>
          </FormControl>
        ) : (
          <TextField
            fullWidth
            size="small"
            label="Shiprocket Pickup Location Name"
            value={pickupLocation}
            onChange={(e) => setPickupLocation(e.target.value)}
            disabled={loading}
            helperText="Pickup address name configured in your Shiprocket panel (default: Primary)"
            sx={{ mb: 2 }}
          />
        )}

        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Package Dimensions &amp; Weight
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Weight (kg)"
              type="number"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              disabled={loading}
              inputProps={{ min: 0.1, step: 0.1 }}
            />
          </Grid>
          <Grid item xs={4} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Length (cm)"
              type="number"
              value={length}
              onChange={(e) => setLength(e.target.value)}
              disabled={loading}
              inputProps={{ min: 1, step: 1 }}
            />
          </Grid>
          <Grid item xs={4} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Width (cm)"
              type="number"
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              disabled={loading}
              inputProps={{ min: 1, step: 1 }}
            />
          </Grid>
          <Grid item xs={4} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Height (cm)"
              type="number"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              disabled={loading}
              inputProps={{ min: 1, step: 1 }}
            />
          </Grid>
        </Grid>

        <Button
          variant="contained"
          color="primary"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <ShippingIcon />}
          onClick={handleCreateShipment}
          disabled={loading}
          fullWidth
          sx={{ py: 1, fontWeight: 600 }}
        >
          {loading ? 'Creating Shipment...' : `Generate ${carrier === 'delhivery' ? 'Delhivery' : 'Shiprocket'} Shipment`}
        </Button>
      </CardContent>
    </Card>
  );
};

export default ShipmentSection;
